import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, openAuthedFile } from '../lib/api';
import {
  Card, CheckField, Empty, ErrorNote, Loading, Modal, Pill, SelectField, Status, Tabs,
  TextField, errorMessage, useToast,
} from '../components/ui';
import { shortDate, type WaiverDocument } from '@lcc/shared';
import { WaiverEditor } from './WaiverEditor';
import { PageHeader } from '../App';

interface Requirement {
  id: string; seasonId: string | null;
  code: string; title: string; version: string; audience: string;
  provider: string; providerRef: string | null;
  /** As stored — a path on the club site, or an absolute URL. */
  documentUrl: string | null;
  /** The same thing resolved, which is what can actually be opened from here. */
  documentHref: string | null;
  requiresGuardian: boolean; expiresOn: string | null; isActive: boolean;
  sortOrder: number;
  signatureCount: number;
  /** The editable waiver, or null when this one is still a link to a PDF. */
  document: WaiverDocument | null;
}

const AUDIENCES = [
  { value: 'all', label: 'Everyone' },
  { value: 'adult', label: 'Adults only (18 and over)' },
  { value: 'youth', label: 'Youth only (under 18)' },
  { value: 'guardian', label: 'Parent or guardian of a youth member' },
  { value: 'volunteer', label: 'Volunteers' },
] as const;

function audienceLabel(value: string): string {
  return AUDIENCES.find((a) => a.value === value)?.label ?? value;
}
interface SignatureRow {
  signatureId: string; memberId: string; memberName: string; memberEmail: string | null;
  code: string; title: string; version: string; state: string;
  signedAt: string | null; typedName: string | null; signedByName: string | null;
  isGuardian: boolean; verificationCode: string | null; overrideReason: string | null;
  provider: string; hasDocument: boolean; documentBytes: number | null;
  documentSha256: string | null; ipAddress: string | null;
}
interface Unattached {
  signatureId: string; requirementId: string;
  code: string; title: string; version: string;
  signerName: string; signerEmail: string | null;
  signerDob: string | null; signerPhone: string | null;
  signedAt: string | null; source: string; verificationCode: string | null;
  hasDocument: boolean;
  suggestion: {
    memberId: string; name: string; dateOfBirth: string | null; exact: boolean;
  } | null;
}

interface OutstandingMember {
  memberId: string; fullName: string; email: string | null; phone: string | null;
  outstandingCount: number;
  outstanding: Array<{ requirementId: string; code: string; title: string; state: string }>;
}

export function WaiversPage() {
  const [tab, setTab] = useState<
    'outstanding' | 'signed' | 'unattached' | 'requirements'>('outstanding');
  const [search, setSearch] = useState('');
  const [override, setOverride] = useState<{ member: OutstandingMember } | null>(null);
  // `null` means the editor is closed; a requirement opens it on that waiver,
  // and 'new' opens it empty.
  const [editing, setEditing] = useState<Requirement | 'new' | null>(null);

  const reqs = useQuery({
    queryKey: ['waivers', 'requirements'],
    queryFn: () => api.get<{ provider: string; data: Requirement[] }>('/waivers/requirements'),
  });
  const unattached = useQuery({
    queryKey: ['waivers', 'unattached'],
    queryFn: () => api.get<{ data: Unattached[] }>('/waivers/unattached'),
  });
  const outstanding = useQuery({
    queryKey: ['waivers', 'outstanding'],
    queryFn: () => api.get<{ count: number; data: OutstandingMember[] }>('/waivers/outstanding'),
  });

  const signed = useQuery({
    queryKey: ['waivers', 'signatures', search],
    queryFn: () => api.get<{ count: number; data: SignatureRow[] }>(
      `/waivers/signatures?q=${encodeURIComponent(search)}`),
  });

  const reconcile = useMutation({
    mutationFn: () => api.post<{ checked: number; updated: number; expired: number; note?: string }>(
      '/waivers/reconcile', { sinceHours: 168 }),
  });
  const toast = useToast();
  const qc = useQueryClient();

  const provider = reqs.data?.provider ?? 'internal';

  return (
    <>
      <PageHeader
        title="Waivers"
        sub={provider === 'waiver_studio'
          ? 'Signed with Waiver Studio · clearance cached here'
          : 'Signed in the portal · recorded here'}
        actions={provider === 'waiver_studio' ? (
          <button className="btn" type="button" disabled={reconcile.isPending}
            onClick={() => reconcile.mutate(undefined, {
              onSuccess: (r) => {
                void qc.invalidateQueries({ queryKey: ['waivers'] });
                toast.success(r.note ?? `Checked ${r.checked}, updated ${r.updated}.`);
              },
              onError: (e) => toast.error(errorMessage(e)),
            })}>
            {reconcile.isPending ? 'Syncing…' : 'Sync with Waiver Studio'}
          </button>
        ) : (
          <button className="btn btn--accent" type="button" onClick={() => setEditing('new')}>
            Add a waiver
          </button>
        )}
      />

      <div className="content stack">
        <div className="banner banner--info">
          <strong>Waivers gate the roster, not registration.</strong> Members can register and pay
          with waivers outstanding, but cannot be added to a team until they are cleared.
        </div>

        <Tabs
          active={tab} onChange={setTab}
          tabs={[
            { id: 'outstanding', label: 'Outstanding', count: outstanding.data?.count },
            { id: 'signed', label: 'Signed', count: signed.data?.count },
            { id: 'unattached', label: 'Unattached', count: unattached.data?.data.length },
            { id: 'requirements', label: 'Requirements', count: reqs.data?.data.length },
          ]}
        />

        {tab === 'outstanding' && (
          outstanding.isLoading ? <Loading />
            : outstanding.error ? <ErrorNote error={outstanding.error} />
            : !outstanding.data?.count
              ? <Card><Empty title="Everyone is cleared">
                  No member has an outstanding waiver.
                </Empty></Card>
              : (
                <Card flush>
                  <div className="table-wrap">
                    <table className="t">
                      <thead>
                        <tr><th>Member</th><th>Contact</th><th>Outstanding</th><th /></tr>
                      </thead>
                      <tbody>
                        {outstanding.data.data.map((m) => (
                          <tr key={m.memberId}>
                            <td><Link to={`/members/${m.memberId}`}>{m.fullName}</Link></td>
                            <td className="small muted">
                              {m.email ?? '—'}{m.phone ? ` · ${m.phone}` : ''}
                            </td>
                            <td className="row row--tight">
                              {m.outstanding.map((o) => (
                                <Pill key={o.requirementId} tone="warn">{o.code}</Pill>
                              ))}
                            </td>
                            <td className="right">
                              <button className="btn btn--sm" type="button"
                                onClick={() => setOverride({ member: m })}>
                                Record paper form
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )
        )}

        {tab === 'signed' && (
          <>
            <Card>
              <TextField
                label="Find a signature"
                value={search}
                onChange={setSearch}
                hint="Search by member, email address, waiver, or the verification code printed on a signed copy."
              />
            </Card>
            {signed.isLoading ? <Loading />
              : signed.error ? <ErrorNote error={signed.error} />
              : !signed.data?.count
                ? <Card><Empty title="Nothing found">
                    {search ? 'No signature matches that.' : 'No waiver has been signed yet.'}
                  </Empty></Card>
                : (
                  <Card flush>
                    <div className="table-wrap">
                      <table className="t">
                        <thead>
                          <tr>
                            <th>Member</th><th>Waiver</th><th>Version</th><th>Signed</th>
                            <th>Signed by</th><th>Code</th><th>Copy</th>
                          </tr>
                        </thead>
                        <tbody>
                          {signed.data.data.map((r) => (
                            <tr key={r.signatureId}>
                              <td><Link to={`/members/${r.memberId}`}>{r.memberName}</Link></td>
                              <td>{r.title}</td>
                              <td className="mono">{r.version}</td>
                              <td className="small">{shortDate(r.signedAt)}</td>
                              <td className="small">
                                {r.state === 'waived'
                                  ? <Pill tone="warn">office override</Pill>
                                  : (r.typedName ?? r.signedByName ?? '—')}
                                {r.isGuardian && <span className="muted"> · guardian</span>}
                                {r.overrideReason && (
                                  <div className="small muted">{r.overrideReason}</div>
                                )}
                              </td>
                              <td className="mono small">{r.verificationCode ?? '—'}</td>
                              <td>
                                {r.hasDocument
                                  ? <DocumentButton row={r} />
                                  : <span className="small muted">paper / no file</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ padding: '.9rem 1.15rem', borderTop: '1px solid var(--border)' }}>
                      <p className="small muted" style={{ margin: 0 }}>
                        A signed copy carries the drawn signature, the typed name, the date, the
                        address it was signed from, and a verification code. Rows marked
                        <b> office override</b> have no member signature — somebody here recorded a
                        paper form, and the reason is shown.
                      </p>
                    </div>
                  </Card>
                )}
          </>
        )}

        {tab === 'unattached' && (
          unattached.isLoading ? <Loading />
            : unattached.error ? <ErrorNote error={unattached.error} />
            : !unattached.data?.data.length
              ? <Card><Empty title="Nothing waiting">
                  Every signature is attached to a member. Waivers signed from the public
                  link land here when the club cannot place who signed them.
                </Empty></Card>
              : (
                <Card flush>
                  <div className="table-wrap">
                    <table className="t">
                      <thead>
                        <tr><th>Signed by</th><th>Waiver</th><th>Signed</th>
                          <th>Code</th><th>Looks like</th><th /></tr>
                      </thead>
                      <tbody>
                        {unattached.data.data.map((u) => (
                          <UnattachedRow key={u.signatureId} row={u} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ padding: '.9rem 1.15rem', borderTop: '1px solid var(--border)' }}>
                    <p className="small muted" style={{ margin: 0 }}>
                      These were signed from the public link by someone the club could not
                      place automatically — a visitor, or a member whose email or date of
                      birth does not match what we hold. Attaching one is what clears that
                      member for the ice, so it is recorded against whoever does it.
                    </p>
                  </div>
                </Card>
              )
        )}

        {tab === 'requirements' && (
          reqs.isLoading ? <Loading /> : (
            <Card flush>
              <div className="table-wrap">
                <table className="t">
                  <thead>
                    <tr><th>Code</th><th>Title</th><th>Version</th><th>Applies to</th>
                      <th>Guardian</th><th>Document</th><th>Active</th><th /></tr>
                  </thead>
                  <tbody>
                    {reqs.data?.data.map((r) => (
                      <tr key={r.id}>
                        <td className="mono">{r.code}</td>
                        <td>
                          <button className="link" type="button" onClick={() => setEditing(r)}>
                            {r.title}
                          </button>
                        </td>
                        <td className="mono">{r.version}</td>
                        <td className="muted small">{audienceLabel(r.audience)}</td>
                        <td>{r.requiresGuardian ? <Pill tone="warn">required</Pill> : '—'}</td>
                        <td>
                          {r.documentHref
                            ? <a href={r.documentHref} target="_blank" rel="noreferrer">Open ↗</a>
                            : <span className="small muted">none attached</span>}
                        </td>
                        <td><Status value={r.isActive ? 'active' : 'cancelled'} /></td>
                        <td className="right">
                          <button className="btn btn--sm" type="button"
                            onClick={() => setEditing(r)}>
                            View &amp; edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '.9rem 1.15rem', borderTop: '1px solid var(--border)' }}>
                <p className="small muted" style={{ margin: 0 }}>
                  Publishing a new <b>version</b> of a code deactivates the old one, which makes
                  every previous signature stop counting — that is how you force a re-sign after
                  amending a waiver.
                </p>
              </div>
            </Card>
          )
        )}
      </div>

      {override && (
        <OverrideModal member={override.member} onClose={() => setOverride(null)} />
      )}

      {editing && (
        <RequirementModal
          requirement={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

/** Opens a signed PDF, which needs the bearer token the API expects. */
function DocumentButton({ row }: { row: SignatureRow }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  return (
    <button
      className="btn btn--sm"
      type="button"
      disabled={busy}
      title={row.documentSha256 ? `SHA-256 ${row.documentSha256.slice(0, 16)}…` : undefined}
      onClick={() => {
        setBusy(true);
        openAuthedFile(`/waivers/signatures/${row.signatureId}/document`)
          .catch((e) => toast.error(errorMessage(e)))
          .finally(() => setBusy(false));
      }}
    >
      {busy ? 'Opening…' : 'Open PDF'}
    </button>
  );
}

/* ------------------------------------------------------- view and edit ---- */


/**
 * One signature from the public link, waiting to be matched to a member.
 *
 * The suggestion is advisory and says so: it matches on email alone, which is
 * looser than the email-and-birthdate the system attaches on by itself. That
 * looseness is the point — a human is about to look at it, and a near miss is
 * worth showing. Attaching is still a decision somebody makes.
 */
function UnattachedRow({ row }: { row: Unattached }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [memberId, setMemberId] = useState(row.suggestion?.memberId ?? '');
  /* The suggestion is a starting point, not the only answer. Someone whose
     email the club has wrong, or who signed with a personal address, has no
     suggestion at all — and they are exactly the people this queue is for. */
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState(row.signerName);

  const matches = useQuery({
    queryKey: ['members', 'search', search],
    queryFn: () => api.get<{ data: Array<{ id: string; fullName: string; email: string | null;
      dateOfBirth: string | null }> }>(
      `/members?perPage=8&q=${encodeURIComponent(search.trim())}`),
    enabled: picking && search.trim().length >= 2,
  });

  const attach = useMutation({
    mutationFn: () => api.post(`/waivers/signatures/${row.signatureId}/attach`, { memberId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['waivers'] });
      toast.success(`${row.signerName}'s ${row.title} is now on their record.`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <tr>
      <td>
        {row.signerName}
        <div className="small muted">
          {row.signerEmail}
          {row.signerDob ? ` · born ${row.signerDob}` : ' · no date of birth given'}
        </div>
      </td>
      <td>
        {row.title}
        <div className="small muted mono">v{row.version}</div>
      </td>
      <td className="small muted">{row.signedAt ? shortDate(row.signedAt) : '—'}</td>
      <td className="mono small">{row.verificationCode ?? '—'}</td>
      <td>
        {row.suggestion ? (
          <>
            <Link to={`/members/${row.suggestion.memberId}`}>{row.suggestion.name}</Link>
            <div className="small muted">
              {row.suggestion.exact
                ? 'email and date of birth both match'
                : 'email matches, date of birth does not'}
            </div>
          </>
        ) : (
          <span className="small muted">no member with that email</span>
        )}
      </td>
      <td className="right">
        {picking ? (
          <div style={{ minWidth: '15rem', textAlign: 'left' }}>
            <input
              autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search members by name or email"
              style={{ width: '100%' }}
            />
            <div style={{ marginTop: '.35rem', display: 'grid', gap: '.2rem' }}>
              {matches.data?.data.slice(0, 6).map((m) => (
                <button
                  key={m.id} type="button"
                  className={m.id === memberId ? 'btn btn--sm btn--accent' : 'btn btn--sm'}
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => setMemberId(m.id)}
                >
                  {m.fullName}
                  {m.dateOfBirth ? ` · ${m.dateOfBirth}` : ''}
                </button>
              ))}
              {matches.data && matches.data.data.length === 0 && (
                <span className="small muted">No member matches that.</span>
              )}
            </div>
            <div className="row row--tight" style={{ marginTop: '.5rem' }}>
              <button
                className="btn btn--sm btn--accent" type="button"
                disabled={!memberId || attach.isPending}
                onClick={() => attach.mutate()}
              >
                {attach.isPending ? 'Attaching…' : 'Attach'}
              </button>
              <button className="btn btn--sm" type="button" onClick={() => setPicking(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="row row--tight" style={{ justifyContent: 'flex-end' }}>
            {row.suggestion && (
              <button
                className="btn btn--sm btn--accent" type="button"
                disabled={attach.isPending}
                onClick={() => attach.mutate()}
                title={`Attach this signature to ${row.suggestion.name}, clearing them`}
              >
                {attach.isPending ? 'Attaching…' : 'Attach'}
              </button>
            )}
            <button className="btn btn--sm" type="button" onClick={() => setPicking(true)}>
              {row.suggestion ? 'Someone else…' : 'Find the member…'}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

/**
 * A waiver, viewed and edited in one place.
 *
 * The document itself is shown, because "is this the right form" is the first
 * question anyone in the office actually has, and the answer is in the PDF
 * rather than in its filename.
 *
 * Editing is deliberately split in two, because the two look similar and are
 * not:
 *
 * - **Saving changes** corrects this version in place — a typo in the title, a
 *   replaced document, who it applies to. Signatures already collected stay
 *   valid, which is right when the wording has not changed.
 * - **Publishing a new version** retires this one, and with it every signature
 *   against it. That is the mechanism for forcing a re-sign after the wording
 *   changes, and it is the destructive one, so it asks separately and says how
 *   many signatures it will invalidate.
 */
function RequirementModal(
  { requirement, onClose }: { requirement: Requirement | null; onClose: () => void },
) {
  const qc = useQueryClient();
  const toast = useToast();
  const isNew = requirement === null;

  const [form, setForm] = useState({
    code: requirement?.code ?? '',
    title: requirement?.title ?? '',
    version: requirement?.version ?? new Date().getFullYear() + '-'
      + String((new Date().getFullYear() + 1) % 100).padStart(2, '0'),
    audience: requirement?.audience ?? 'all',
    documentUrl: requirement?.documentUrl ?? '',
    requiresGuardian: requirement?.requiresGuardian ?? false,
    expiresOn: requirement?.expiresOn ?? '',
    isActive: requirement?.isActive ?? true,
    sortOrder: requirement?.sortOrder ?? 0,
  });
  const [doc, setDoc] = useState<WaiverDocument | null>(requirement?.document ?? null);
  const [pane, setPane] = useState<'document' | 'details'>(
    /* The waiver itself is what someone came here to change nine times out of
       ten; the code, the version and the link are settings they set once. */
    requirement?.document ? 'document' : 'details',
  );
  const [newVersion, setNewVersion] = useState('');
  const [publishing, setPublishing] = useState(false);
  const set = <K extends keyof typeof form>(key: K) => (v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: v }));

  const save = useMutation({
    mutationFn: (version: string) => api.post<{ data: Requirement }>('/waivers/requirements', {
      seasonId: requirement?.seasonId ?? null,
      code: form.code.trim(),
      title: form.title.trim(),
      version: version.trim(),
      audience: form.audience,
      provider: requirement?.provider ?? 'internal',
      documentUrl: form.documentUrl.trim() || null,
      document: doc,
      requiresGuardian: form.requiresGuardian,
      expiresOn: form.expiresOn || null,
      isActive: form.isActive,
      sortOrder: form.sortOrder,
    }),
    onSuccess: (_r, version) => {
      void qc.invalidateQueries({ queryKey: ['waivers'] });
      toast.success(
        isNew ? 'Waiver added.'
          : version === requirement!.version ? 'Changes saved.'
          : `Version ${version} published — members will be asked to sign again.`,
      );
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const problems: string[] = [];
  if (form.code.trim().length < 1) problems.push('a code');
  if (form.title.trim().length < 1) problems.push('a title');
  if (form.version.trim().length < 1) problems.push('a version');
  const ready = problems.length === 0 && !save.isPending;

  return (
    <Modal
      wide
      full={pane === 'document'}
      title={isNew ? 'Add a waiver' : requirement!.title}
      onClose={onClose}
      footer={(
        <>
          <button className="btn" type="button" onClick={onClose}>Close</button>
          <button
            className="btn btn--accent" type="button" disabled={!ready}
            onClick={() => save.mutate(form.version)}
          >
            {save.isPending ? 'Saving…' : isNew ? 'Add waiver' : 'Save changes'}
          </button>
        </>
      )}
    >
      {!isNew && pane === 'details' && (
        <div className="wv">
          <div className="wv__doc">
            {requirement!.documentHref ? (
              <>
                <div className="wv__frame">
                  <iframe
                    /* Fit the width and drop the thumbnail rail: in a panel this
                       size the default view opens at about a third scale, which
                       is too small to read a waiver in. */
                    src={`${requirement!.documentHref}#view=FitH&navpanes=0`}
                    title={`${requirement!.title} (version ${requirement!.version})`}
                  />
                </div>
                <p className="small muted" style={{ margin: '.5rem 0 0' }}>
                  <a href={requirement!.documentHref} target="_blank" rel="noreferrer">
                    Open in a new tab ↗
                  </a>
                  {' — some browsers will not show a PDF in a panel this size.'}
                </p>
              </>
            ) : (
              <div className="wv__frame wv__frame--empty">
                <p className="small muted">
                  No document is attached to this waiver. Add its address below and it
                  will appear here and on the website.
                </p>
              </div>
            )}
          </div>

          <div className="wv__facts">
            <dl className="kv">
              <div><dt>Code</dt><dd className="mono">{requirement!.code}</dd></div>
              <div><dt>Version</dt><dd className="mono">{requirement!.version}</dd></div>
              <div><dt>Applies to</dt><dd>{audienceLabel(requirement!.audience)}</dd></div>
              <div><dt>Signatures</dt><dd>{requirement!.signatureCount}</dd></div>
              <div>
                <dt>Status</dt>
                <dd><Status value={requirement!.isActive ? 'active' : 'cancelled'} /></dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      <Tabs
        tabs={[
          { id: 'document' as const, label: 'The waiver' },
          { id: 'details' as const, label: isNew ? 'Details' : 'Details & publishing' },
        ]}
        active={pane}
        onChange={setPane}
      />

      {pane === 'document' && (
        <>
          <p className="small muted" style={{ margin: '.75rem 0 1rem' }}>
            What a member reads and fills in, on the website and in the app. Changes here
            are not saved until you press <b>{isNew ? 'Add waiver' : 'Save changes'}</b>.
          </p>
          <WaiverEditor value={doc} onChange={setDoc} />
        </>
      )}

      {pane === 'details' && (
      <>
      <h3 className="wv__h">{isNew ? 'Details' : 'Edit this version'}</h3>
      <fieldset disabled={save.isPending}>
        <div className="grid grid--2">
          <TextField
            label="Title" value={form.title} onChange={set('title')}
            hint="What members see on the website, in the app and on the signed PDF."
          />
          <TextField
            label="Code" value={form.code} onChange={set('code')}
            hint={isNew
              ? 'A short identifier, e.g. curling-canada-liability. It never changes again.'
              : 'Changing this makes a separate waiver rather than renaming this one.'}
          />
        </div>
        <div className="grid grid--2">
          <SelectField
            label="Applies to" value={form.audience}
            onChange={(v) => set('audience')(v)}
            options={AUDIENCES.map((a) => ({ value: a.value as string, label: a.label }))}
          />
          <TextField
            label="Version" value={form.version} onChange={set('version')}
            hint={isNew
              ? 'Usually the season, e.g. 2026-27.'
              : 'Editing this here renames the version in place and keeps its signatures. '
                + 'To force a re-sign, use Publish a new version below.'}
          />
        </div>
        <TextField
          label="Document address" value={form.documentUrl} onChange={set('documentUrl')}
          hint={<>A path on the club site, e.g. <code>/assets/docs/waiver.pdf</code> (the
            files live in <code>website/&lt;club&gt;/assets/docs/</code>), or a full https:// address.</>}
        />
        <div className="grid grid--2">
          <TextField
            label="Expires on" value={form.expiresOn} onChange={set('expiresOn')} type="date"
            hint="Optional. Leave empty unless the waiver itself has an end date."
          />
          <TextField
            label="Order" value={String(form.sortOrder)}
            onChange={(v) => set('sortOrder')(Number(v) || 0)}
            hint="Lowest first, wherever waivers are listed."
          />
        </div>
        <CheckField
          label="A parent or guardian must sign this"
          checked={form.requiresGuardian} onChange={set('requiresGuardian')}
          hint="Signing then asks for the guardian's name, which is printed on the signed PDF."
        />
        <CheckField
          label="Active — members must sign it"
          checked={form.isActive} onChange={set('isActive')}
          hint={!isNew && !requirement!.isActive && form.isActive
            ? 'This retires whichever version is active now, and its signatures stop counting.'
            : 'Turn this off to retire a waiver without deleting its signing history.'}
        />
      </fieldset>

      {problems.length > 0 && (
        <p className="small" style={{ color: 'var(--accent)' }}>
          Still needs {problems.join(', ')}.
        </p>
      )}

      {!isNew && (
        <>
          <h3 className="wv__h">Publish a new version</h3>
          <div className="banner banner--warn">
            <strong>This forces everyone to sign again.</strong>{' '}
            {requirement!.signatureCount > 0
              ? `${requirement!.signatureCount} signature${requirement!.signatureCount === 1 ? '' : 's'} `
                + `against version ${requirement!.version} will stop counting, and those members `
                + 'cannot be put on a team until they have signed the new one.'
              : 'No signatures are recorded against this version yet, so nothing is lost.'}{' '}
            Do this when the <em>wording</em> changes. For a typo or a replaced file, save
            the changes above instead.
          </div>
          {publishing ? (
            <>
              <TextField
                label="New version" value={newVersion} onChange={setNewVersion}
                placeholder={`e.g. ${requirement!.version} rev B`}
                hint="Any label you like, as long as it is different from the current one."
              />
              <div className="row row--tight">
                <button className="btn" type="button"
                  onClick={() => { setPublishing(false); setNewVersion(''); }}>
                  Cancel
                </button>
                <button
                  className="btn btn--danger" type="button"
                  disabled={
                    save.isPending
                    || newVersion.trim().length < 1
                    || newVersion.trim() === requirement!.version
                  }
                  onClick={() => save.mutate(newVersion)}
                >
                  {save.isPending
                    ? 'Publishing…'
                    : `Publish ${newVersion.trim() || '…'} and require re-signing`}
                </button>
              </div>
            </>
          ) : (
            <button className="btn" type="button" onClick={() => setPublishing(true)}>
              Publish a new version…
            </button>
          )}
        </>
      )}
      </>
      )}
    </Modal>
  );
}

function OverrideModal({ member, onClose }: { member: OutstandingMember; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState('Signed paper form on file in the office');

  const run = useMutation({
    mutationFn: async () => {
      for (const o of member.outstanding) {
        await api.post(`/waivers/${o.requirementId}/override`, { memberId: member.memberId, reason });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['waivers'] });
      void qc.invalidateQueries({ queryKey: ['clearance', member.memberId] });
      toast.success(`${member.fullName} cleared.`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Modal
      title={`Clear ${member.fullName} by hand`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn--accent" type="button"
            disabled={run.isPending || reason.trim().length < 3}
            onClick={() => run.mutate()}>
            {run.isPending ? 'Recording…' : `Clear ${member.outstanding.length} waiver(s)`}
          </button>
        </>
      }
    >
      <div className="stack">
        {run.error ? <ErrorNote error={run.error} /> : null}
        <div className="banner banner--warn">
          This records the waivers as <b>waived</b> rather than signed. Your name and the reason
          are written to the audit log.
        </div>
        <ul className="small">
          {member.outstanding.map((o) => <li key={o.requirementId}>{o.title}</li>)}
        </ul>
        <TextField label="Reason" value={reason} onChange={setReason}
          hint="Required. Say where the signed form is." />
      </div>
    </Modal>
  );
}
